import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/ui/Header2";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import ShippingForm from "./components/ShippingForm";
import PaymentForm from "./components/PaymentForm";
import OrderSummary from "./components/OrderSummary";
import OrderNotes from "./components/OrderNotes";
import CheckoutProgress from "./components/CheckoutProgress";
import { useDispatch, useSelector } from "react-redux";
import {
  getMyCartService,
  getProvincesService,
} from "redux/services/general_service";
import { toast } from "react-toastify";
import axios from "axios";
import * as URLS from "../../../urls";

const Checkout = () => {
  const { t, i18n } = useTranslation();

  const { cartList } = useSelector((state) => state.general);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderData, setOrderData] = useState({
    shipping: {},
    payment: {},
    notes: {},
    totals: {},
  });
  const [formValidation, setFormValidation] = useState({
    shipping: false,
    payment: false,
  });

  // Mock saved addresses for authenticated users
  const mockSavedAddresses = [
    {
      id: 1,
      first_name: "John",
      last_name: "Doe",
      address: "123 Main Street, Apt 4B",
      city: "New York",
      state: "NY",
      zipCode: "10001",
      country: "US",
      phone: "+1 (555) 123-4567",
      isDefault: true,
    },
    {
      id: 2,
      first_name: "John",
      last_name: "Doe",
      address: "456 Oak Avenue",
      city: "Brooklyn",
      state: "NY",
      zipCode: "11201",
      country: "US",
      phone: "+1 (555) 123-4567",
      isDefault: false,
    },
  ];

  useEffect(() => {
    // Check authentication status
    const authStatus = localStorage.getItem("isAuthenticated");
    setIsAuthenticated(authStatus === "true");

    if (authStatus !== "true") {
      navigate("/login");
    } else if (cartList.length === 0) {
      navigate("/");
    }
  }, [navigate, cartList.length]);

  const handleShippingFormChange = (formData, isValid) => {
    setOrderData((prev) => ({
      ...prev,
      shipping: formData,
    }));
    setFormValidation((prev) => ({
      ...prev,
      shipping: isValid,
    }));
  };

  const handlePaymentChange = (paymentData) => {
    setOrderData((prev) => ({
      ...prev,
      payment: paymentData,
    }));
    setFormValidation((prev) => ({
      ...prev,
      payment: paymentData.isValid,
    }));
  };

  const handleNotesChange = (notesData) => {
    setOrderData((prev) => ({
      ...prev,
      notes: notesData,
    }));
  };

  const handleTotalChange = (totals) => {
    setOrderData((prev) => ({
      ...prev,
      totals,
    }));
  };

  const handleContinueToPayment = () => {
    if (formValidation.shipping) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleContinueToReview = () => {
    if (formValidation.payment) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBackToShipping = () => {
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToPayment = () => {
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePlaceOrder = async () => {
    setIsProcessing(true);
    const cart_ids = cartList.map((item) => item.cart_id);
    try {
      const response = await axios.post(
        `${URLS.baseUrl}/${URLS?.ordersURL}`,
        {
          ...orderData?.shipping,
          notes: orderData?.notes?.notes,
          cart_ids: cart_ids,
         province_id: orderData?.shipping?.province,
          discount_coupon_code: localStorage.getItem("discount_coupon_code"),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      );

      if (response?.status === 200 || response?.status === 201) {
        toast.success(t("orderPlaced"));
        localStorage.removeItem("cartItems");
        localStorage.setItem("cartCount", "0");
        localStorage.removeItem("discount_coupon_code");
        dispatch(getMyCartService());
        window.location.href = "/order-history"
       // navigate("/order-history");
      } else {
        setIsProcessing(false);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 422) {
          const validationErrors = error.response.data.errors;
          if (validationErrors) {
            Object.keys(validationErrors).forEach((key) => {
              validationErrors[key].forEach((message) => {
                toast.error(t(message));
              });
            });
          }
        } else {
          toast.error(t("orderFailed"));
        }
      } else {
        toast.error(t("orderFailed"));
      }
      setIsProcessing(false);
    }
  };

  const canProceedToPayment = formValidation.shipping;
  const canProceedToReview = formValidation.payment;
  const canPlaceOrder = formValidation.shipping && formValidation.payment;

  useEffect(() => {
    dispatch(getProvincesService(1));
    dispatch(getMyCartService());
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-8">
          <Link
            to="/home-page"
            className="hover:text-indigo-600 transition"
          >
            {t("home")}
          </Link>
          <Icon name="ChevronRight" size={16} className="text-gray-400" />
          <Link
            to="/shopping-cart"
            className="hover:text-indigo-600 transition"
          >
            {t("cart")}
          </Link>
          <Icon name="ChevronRight" size={16} className="text-gray-400" />
          <span className="text-gray-900 font-semibold">{t("secureCheckout")}</span>
        </nav>

        {/* Page Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-3">
            {t("secureCheckout")}
          </h1>
          <p className="text-gray-600 text-lg">{t("completeOrder")}</p>
        </div>

        {/* Progress Indicator */}
        <CheckoutProgress currentStep={currentStep} className="mb-10" />

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Forms Section */}
          <div className="lg:col-span-2 space-y-10">
            {/* Step 1: Shipping Information */}
            {currentStep === 1 && (
              <div className="bg-white border border-gray-200 rounded-[4px] p-8 shadow-lg">
                <ShippingForm
                  onFormChange={handleShippingFormChange}
                  savedAddresses={isAuthenticated ? mockSavedAddresses : []}
                  isAuthenticated={isAuthenticated}
                />

                <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-300">
                  <Link
                    to="/shopping-cart"
                    className="flex items-center space-x-2 text-gray-500 hover:text-indigo-600 transition"
                  >
                    <Icon
                      name={i18n.language === "en" ? "ArrowLeft" : "ArrowRight"}
                      size={18}
                      className="text-gray-500"
                    />
                    <span>{t("backToCart")}</span>
                  </Link>

                  <Button
              className="rounded-[4px]"
                    onClick={handleContinueToPayment}
                    disabled={!canProceedToPayment}
                
                  >
                    {t("continueToPayment")}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Payment Information */}
            {currentStep === 2 && (
              <div className="bg-white border border-gray-200 rounded-[4px] p-8 shadow-lg">
                <PaymentForm
                  onPaymentChange={handlePaymentChange}
                  orderTotal={orderData.totals.total}
                />

                <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-300">
                  <Button
                    variant="outline"
                    onClick={handleBackToShipping}
                    iconName={
                      i18n.language === "en" ? "ArrowLeft" : "ArrowRight"
                    }
                    iconPosition={i18n.language === "en" ? "left" : "right"}
                    className="rounded-[4px] px-6 py-3 text-indigo-600 border-indigo-600 rounded-[4px] hover:bg-indigo-50 transition"
                  >
                    {t("backToShipping")}
                  </Button>

                  <Button
                    variant="default"
                    onClick={handleContinueToReview}
                    disabled={!canProceedToReview}
                    className="rounded-[4px]"
                  //  className="px-6 py-3 bg-indigo-600 text-white rounded-[4px] shadow-md hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("reviewOrder")}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Order Review */}
            {currentStep === 3 && (
              <div className="space-y-8">
                {/* Order Review Summary */}
                <div className="bg-white border border-gray-200 rounded-[4px] p-8 shadow-lg max-w-3xl mx-auto">
                  <div className="flex items-center space-x-3 mb-8">
                    <Icon
                      name="CheckCircle"
                      size={24}
                      className="text-green-500"
                    />
                    <h2 className="text-2xl font-extrabold text-gray-900">
                      {t("reviewOrder")}
                    </h2>
                  </div>

                  {/* Shipping Details Review */}
                  <div className="mb-8">
                    <h3 className="font-semibold text-gray-800 mb-4 border-b border-gray-300 pb-2">
                      {t("shippingInformation")}
                    </h3>
                    <div className="bg-gray-50 rounded-[4px] p-6 shadow-inner">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700">
                        <div>
                          <span className="text-gray-500">{t("name")}:</span>
                          <div className="font-medium text-gray-900 mt-1">
                            {orderData.shipping.first_name}{" "}
                            {orderData.shipping.last_name}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">{t("email")}:</span>
                          <div className="font-medium text-gray-900 mt-1">
                            {orderData.shipping.email}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">{t("phone")}:</span>
                          <div className="font-medium text-gray-900 mt-1">
                            {orderData.shipping.phone}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">{t("address")}:</span>
                          <div className="font-medium text-gray-900 mt-1 leading-relaxed">
                            {orderData.shipping.address}
                            <br />
                            {orderData.shipping.city},{" "}
                            {orderData.shipping.province_id}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Method Review */}
                  <div className="mb-8">
                    <h3 className="font-semibold text-gray-800 mb-4 border-b border-gray-300 pb-2">
                      {t("paymentMethod")}
                    </h3>
                    <div className="bg-gray-50 rounded-[4px] p-6 shadow-inner flex items-center space-x-4">
                      <Icon
                        name={
                          orderData.payment.method === "card"
                            ? "CreditCard"
                            : orderData.payment.method === "paypal"
                            ? "Wallet"
                            : "Banknote"
                        }
                        size={24}
                        className="text-indigo-600"
                      />
                      <div>
                        <div className="font-medium text-gray-900">
                          {orderData.payment.method === "card" &&
                            t("creditDebitCard")}
                          {orderData.payment.method === "paypal" && t("payPal")}
                          {orderData.payment.method === "cod" &&
                            t("cashOnDelivery")}
                        </div>
                        {orderData.payment.method === "card" &&
                          orderData.payment.data?.cardNumber && (
                            <div className="text-sm text-gray-500 mt-1">
                              •••• •••• ••••{" "}
                              {orderData.payment.data.cardNumber.slice(-4)}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-6 border-t border-gray-300">
                    <Button
                      variant="outline"
                      onClick={handleBackToPayment}
                      iconName={
                        i18n.language === "en" ? "ArrowLeft" : "ArrowRight"
                      }
                      iconPosition={i18n.language === "en" ? "left" : "right"}
                      className="rounded-[4px] px-6 py-3 text-indigo-600 border-indigo-600 hover:bg-indigo-50 transition"
                    >
                      {t("backToPayment")}
                    </Button>

                    <Button
                      variant="default"
                      onClick={handlePlaceOrder}
                      disabled={!canPlaceOrder}
                      loading={isProcessing}
                      iconName="ShoppingBag"
                      iconPosition="left"
                      className='rounded-[4px]'
                   //   className="px-6 py-3 bg-indigo-600 text-white rounded-[4px] shadow-md hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing
                        ? t("processingOrder")
                        : t("placeOrder", {
                            total: orderData.totals.total?.toFixed(2) || "0.00",
                          })}
                    </Button>
                  </div>
                </div>

                {/* Order Notes */}
                <div className="bg-white border border-gray-200 rounded-[4px] p-8 shadow-lg">
                  <OrderNotes onNotesChange={handleNotesChange} />
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <OrderSummary
                cartItems={cartList}
                onTotalChange={handleTotalChange}
                currentStep={currentStep}
              />
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 text-sm text-gray-500">
            <Icon name="Lock" size={16} className="text-green-500" />
            <span>{t("paymentSecure")}</span>
          </div>
        </div>
      </div>

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[4px] p-8 max-w-sm mx-4 text-center shadow-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("processingOrder")}
            </h3>
            <p className="text-sm text-gray-600">{t("pleaseDontClose")}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
